'use client';

/**
 * The on-screen keyboard.
 *
 * It does four jobs at once, which is why it is worth a dedicated component:
 *   • shows which key to press next (pulsing, in that key's finger colour)
 *   • shows the Shift key to hold, on the correct side
 *   • echoes the child's actual presses, right or wrong
 *   • teaches the finger→key mapping passively, by tinting every key with the
 *     colour of the finger that owns it
 *
 * It renders as plain divs rather than an SVG so the layout stays fluid and
 * every key can be a real accessibility node.
 */
import React, { memo } from 'react';
import { FINGERS, KEY_ROWS, keyForChar, shiftKeyForChar, fingerForChar } from '../lib/keyboard';
import type { KeyDef } from '../types';

export interface KeyboardProps {
  /** The character the child should type next — drives the highlight. */
  nextChar?: string;
  /** Key ids currently held down, echoed from real key events. */
  pressed?: Set<string>;
  /** Set briefly after a miss so the wrong key can shake. */
  wrongKeyId?: string | null;
  /** Dim keys the child has not been taught yet. */
  availableKeys?: string[];
  /** Paint every key in its finger's colour. Off during boss lessons. */
  showFingerColors?: boolean;
  /**
   * Fill taught keys with solid finger colour rather than a tint. Used during
   * lessons, where the finger→key mapping has to be unmissable.
   */
  vivid?: boolean;
  /** Emoji that perches on the next key. Pure delight; carries no meaning. */
  mascot?: string;
  /** Cosmetic skin id from the reward shop. */
  skin?: string;
  /** Compact rendering for the level pages and dashboards. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Key sizes. The minimums matter more than the maximums: a keyboard row is
 * 15 key-units wide, so on a 375px phone each unit has about 20px to live in
 * once padding and gaps are taken out. A larger floor pushes the row past the
 * viewport and makes the whole page scroll sideways.
 */
/**
 * Key *height* and label scale. Width is handled by flex proportion, so these
 * only need to keep the caps looking roughly square at each breakpoint.
 */
const SIZES: Record<NonNullable<KeyboardProps['size']>, string> = {
  sm: 'clamp(16px, 2.4vw, 30px)',
  md: 'clamp(18px, 3.4vw, 42px)',
  lg: 'clamp(20px, 4vw, 50px)',
  xl: 'clamp(22px, 5.2vw, 56px)',
};

/** Skins only ever change colour, never layout — they are purely cosmetic. */
const SKIN_STYLES: Record<string, React.CSSProperties> = {
  classic: {},
  candy: { ['--k-key-bg' as string]: 'linear-gradient(180deg,#fff,#FFF0F6)' },
  neon: { ['--k-key-bg' as string]: '#141726', ['--k-key-fg' as string]: '#7DF9FF' },
  wood: { ['--k-key-bg' as string]: 'linear-gradient(180deg,#F3E2CB,#E4CBA6)' },
  gold: { ['--k-key-bg' as string]: 'linear-gradient(180deg,#FFE9A8,#E9B949)' },
};

function Key({
  def,
  isNext,
  isShiftTarget,
  isPressed,
  isWrong,
  isAvailable,
  isTaught,
  showFingerColors,
  vivid,
  mascot,
  keySize,
}: {
  def: KeyDef;
  isNext: boolean;
  isShiftTarget: boolean;
  isPressed: boolean;
  isWrong: boolean;
  isAvailable: boolean;
  isTaught: boolean;
  showFingerColors: boolean;
  vivid: boolean;
  mascot?: string;
  keySize: string;
}) {
  const finger = FINGERS[def.finger];
  const fingerColor = `var(${finger.cssVar})`;
  const highlight = isNext || isShiftTarget;

  // Vivid fill is for the keys the lesson is actually about. Tab, Caps, Shift,
  // Enter, Backspace and the space bar are always *usable*, but colouring them
  // makes them shout louder than the letters — on a Level 1 board a hot pink
  // SHIFT drowns out the two keys the child is there to find. They stay quiet.
  const vividFill = vivid && isTaught && !isWrong;

  const classes = [
    'k-key',
    highlight && 'k-key-next',
    vividFill && 'k-key-vivid',
    isPressed && !highlight && 'k-key-pressed',
    isWrong && 'k-key-wrong',
    !vivid && showFingerColors && !highlight && !isWrong && 'k-key-fingered',
    def.anchor && 'k-key-anchor',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      style={{
        ['--k-finger' as string]: fingerColor,
        ['--k-next' as string]: fingerColor,
        ['--k-key-size' as string]: keySize,
        // Proportional sizing: grow by the key's unit count from a zero basis,
        // so every row divides its width 15 ways however wide the row is.
        flexGrow: def.width ?? 1,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        opacity: isAvailable ? 1 : 0.28,
        background: highlight || isWrong || vividFill ? undefined : 'var(--k-key-bg, var(--card))',
        color: highlight || isWrong || vividFill ? undefined : 'var(--k-key-fg, inherit)',
      }}
      aria-hidden="true"
      data-key={def.id}
    >
      {isNext && mascot && <span className="k-key-mascot">{mascot}</span>}
      {def.shiftLabel && def.id.length === 1 ? (
        <span className="flex flex-col items-center leading-none gap-[1px]">
          <span style={{ fontSize: '0.72em', opacity: 0.7 }}>{def.shiftLabel}</span>
          <span>{def.label}</span>
        </span>
      ) : (
        def.label
      )}
    </div>
  );
}

const MemoKey = memo(Key);

/**
 * A "taught" key is a single printable character the curriculum has introduced.
 * The space bar is excluded: it is pressed with a thumb, not one of the eight
 * fingers, and filling a nine-unit-wide slab with colour unbalances the board.
 */
function isTaughtKey(id: string, available: Set<string> | null): boolean {
  if (id.length !== 1 || id === ' ') return false;
  return !available || available.has(id);
}

function KidsKeyboard({
  nextChar,
  pressed,
  wrongKeyId,
  availableKeys,
  showFingerColors = true,
  vivid = false,
  mascot,
  skin = 'classic',
  size = 'md',
  className = '',
}: KeyboardProps) {
  const nextKey = nextChar ? keyForChar(nextChar) : null;
  const shiftTarget = nextChar ? shiftKeyForChar(nextChar) : null;
  const keySize = SIZES[size];
  const available = availableKeys ? new Set([...availableKeys, ' ']) : null;

  // Modifier and navigation keys are always usable, whatever the level.
  const alwaysOn = new Set([
    'Backspace',
    'Tab',
    'CapsLock',
    'Enter',
    'ShiftLeft',
    'ShiftRight',
    ' ',
  ]);

  return (
    <div
      className={`flex flex-col gap-[clamp(3px,0.45vw,6px)] select-none ${className}`}
      style={SKIN_STYLES[skin] ?? SKIN_STYLES.classic}
      role="img"
      aria-label={
        nextChar
          ? `Keyboard. Next key: ${nextChar === ' ' ? 'space bar' : nextChar}, typed with the ${
              FINGERS[fingerForChar(nextChar)].label
            }.`
          : 'On-screen keyboard showing which finger types each key.'
      }
    >
      {KEY_ROWS.map((row, rowIndex) => (
        <div className="k-kbd-row" key={`kbd-row-${rowIndex}`}>
          {row.map((def) => (
            <MemoKey
              key={def.id}
              def={def}
              keySize={keySize}
              isNext={!!nextKey && def.id === nextKey.id}
              isShiftTarget={shiftTarget === def.id}
              isPressed={!!pressed?.has(def.id)}
              isWrong={wrongKeyId === def.id}
              isAvailable={!available || alwaysOn.has(def.id) || available.has(def.id)}
              isTaught={isTaughtKey(def.id, available)}
              showFingerColors={showFingerColors}
              vivid={vivid}
              mascot={mascot}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default memo(KidsKeyboard);

/** Legend mapping each colour to a finger — shown under the keyboard. */
export function FingerLegend({ className = '' }: { className?: string }) {
  const order: (keyof typeof FINGERS)[] = [
    'l-pinky',
    'l-ring',
    'l-middle',
    'l-index',
    'thumb',
    'r-index',
    'r-middle',
    'r-ring',
    'r-pinky',
  ];
  return (
    <ul className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-2 ${className}`}>
      {order.map((id) => {
        const finger = FINGERS[id];
        return (
          <li
            key={id}
            className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground"
          >
            <span
              className="inline-block rounded-full"
              style={{ width: 12, height: 12, background: `var(${finger.cssVar})` }}
              aria-hidden="true"
            />
            {finger.label}
          </li>
        );
      })}
    </ul>
  );
}
